import { SkapaElement } from "@ingka/skapa-webc-element";
import { Icon } from "@ingka/icon-webc/passive.js";
import { imageStrikethrough, image } from "@ingka/icon-store";
const styles = ':host{display:block;height:auto;max-width:100%;position:relative}:host slot,:host slot::slotted(*){height:inherit!important;max-width:inherit;object-fit:inherit;width:inherit}';
const fallbackStyles = ':host{background:rgb(var(--colour-neutral-2,245,245,245));padding-bottom:100%}:host .icon{color:rgb(var(--colour-text-and-icon-4,146,146,146));left:50%;position:absolute;top:50%;transform:translate(-50%,-50%)}:host slot{clip:rect(1px,1px,1px,1px);display:block;height:1px;margin:-1px;overflow:hidden;position:absolute;white-space:nowrap;width:1px}';
function generateTemplate({ html }) {
  const template = document.createElement("template");
  template.innerHTML = html`<style>
      ${styles}</style
    ><slot></slot>`;
  const fallbackStyleElement = document.createElement("style");
  fallbackStyleElement.innerHTML = fallbackStyles;
  return { main: template, fallbackStyle: fallbackStyleElement };
}
class Image extends SkapaElement {
  constructor() {
    super();
    this.state = {
      loadError: false,
      watching: false
    };
    this.showPlaceholder = () => {
      if (!this.fallbackIcon) {
        this.fallbackIcon = document.createElement(
          this.getPrefixedTagName("icon")
        );
        this.fallbackIcon.className = "icon";
      }
      this.fallbackIcon.icon = this.fallback ? "image" : "image-strikethrough";
      if (this.shadowRoot)
        this.shadowRoot.append(
          this.__fallbackStylesNode,
          this.fallbackIcon
        );
    };
    this.updatePlaceholder = () => {
      if (!this.fallback && !this.state.loadError) {
        this.fallbackIcon?.remove();
        this.__fallbackStylesNode.remove();
      } else if (this.fallbackIcon) {
        this.fallbackIcon.icon = this.fallback ? "image" : "image-strikethrough";
      }
      if (!this.fallback) {
        this.watchAssignedImage();
      }
    };
    this.handleError = () => {
      this.state.loadError = true;
      this.showPlaceholder();
    };
    this.handleLoad = () => {
      this.state.loadError = false;
      this.updatePlaceholder();
    };
    this.handleSlotChange = () => {
      this.state.loadError = false;
      this.updatePlaceholder();
    };
    this.watchAssignedImage = () => {
      const assignedImage = this.querySelector("img");
      if (assignedImage && assignedImage.complete) {
        if (assignedImage.naturalHeight < 1) {
          if (!this.state.loadError) this.handleError();
        } else if (this.state.loadError) {
          this.handleLoad();
        } else {
          this.startWatching();
        }
      } else if (!this.state.watching) {
        this.startWatching();
      }
    };
    // Deduplicated listener launch method
    this.startWatching = () => {
      this.imageSlot.addEventListener("error", this.handleError, true);
      this.imageSlot.addEventListener("load", this.handleLoad, true);
      this.state.watching = true;
    };
    this.ignoreAssignedImage = () => {
      if (!this.state.watching) return;
      this.imageSlot.removeEventListener("error", this.handleError, true);
      this.imageSlot.removeEventListener("load", this.handleLoad, true);
      this.state.watching = false;
    };
    this.gatherProperties(["fallback"], this);
    const shadow = this.attachShadow({ mode: "open" });
    const template = this.getTemplates(generateTemplate);
    shadow.append(template.main.content.cloneNode(true));
    this.__fallbackStylesNode = template.fallbackStyle;
    this.imageSlot = shadow.querySelector("slot");
  }
  connectedCallback() {
    super.connectedCallback();
    this.imageSlot.addEventListener("slotchange", this.handleSlotChange);
    if (this.fallback) this.showPlaceholder();
    else this.watchAssignedImage();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.imageSlot.removeEventListener("slotchange", this.handleSlotChange);
    this.ignoreAssignedImage();
  }
  get fallback() {
    return this.hasAttribute("fallback");
  }
  // Setting fallback mode via property. Effects are delegated to the attributeChangedCallback
  set fallback(val) {
    this.toggleAttribute("fallback", !!val);
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null) this.showPlaceholder();
    else if (newValue === null) this.updatePlaceholder();
  }
}
Image.skapaDependencies = [Icon];
Image.skapaIconDependencies = [imageStrikethrough, image];
Image.skapaTagName = "image";
Image.observedAttributes = ["fallback"];
var Image_default = Image;
export {
  Image,
  Image_default as default
};
